import i18next from "i18next";

import { formatFullDate, formatShortDateWithWeekday } from "@ergon-ai/utils";

/**
 * Translates and formats a date into a contextual, locale-sensitive string.
 *
 * - Returns `"Today"` if the date is **the current day**.
 * - Returns `"Tomorrow"` if the date is **the next day**.
 * - Otherwise, it formats the date using either **long format** (full date) or **short format** (weekday + short date).
 *
 * @param date - The `Date` object to translate and format.
 * @param locale - The locale string (e.g., `"en-GB"`, `"it-IT"`). Defaults to `"en-GB"`.
 * @param format - The format type for the date:
 *   - `"long"` → Uses `formatFullDate()`
 *   - `"short"` → Uses `formatShortDateWithWeekday()`
 *   Defaults to `"long"`.
 * @param options - Optional configuration object.
 * @param options.useAbsoluteDates - If true, always uses absolute dates instead of "Today"/"Tomorrow". Useful for consistent test snapshots.
 * @returns A translated, formatted date string (`"Today"`, `"Tomorrow"`, or a formatted date).
 *
 * @example
 * ```ts
 * getTranslatedDate(new Date()); // "Today"
 * getTranslatedDate(new Date(Date.now() + 86400000)); // "Tomorrow"
 * getTranslatedDate(new Date("2025-02-05T00:00:00Z"), "en-GB", "long"); // "05/02/2025"
 * getTranslatedDate(new Date("2025-02-10T00:00:00Z"), "en-GB", "short"); // "Mon, 10 Feb"
 *
 * // With absolute dates for consistent test snapshots:
 * getTranslatedDate(new Date(), "en-GB", "long", { useAbsoluteDates: true }); // "25/06/2025"
 * ```
 */
export function getTranslatedDate(
  date: Date,
  locale = "en-GB",
  format: "long" | "short" = "long",
  options?: { useAbsoluteDates?: boolean },
): string {
  const now = new Date();

  // Normalize current date (removing time for accurate comparison)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const inputDate = new Date(date);

  // 🔹 If useAbsoluteDates is true, always return formatted date (useful for test consistency)
  if (options?.useAbsoluteDates) {
    const formattedDate =
      format === "long"
        ? formatFullDate(inputDate, locale) // Full date format (e.g., "05/02/2025")
        : formatShortDateWithWeekday(inputDate, locale); // Short format (e.g., "Mon, 10 Feb")

    return i18next.t("date.colloquial", { val: formattedDate }); // Translation key for a formatted date
  }

  // 🔹 If the date is today → Return "Today"
  if (inputDate.toDateString() === today.toDateString()) {
    return i18next.t("date.today"); // Translation key for "Today"
  }

  // 🔹 If the date is tomorrow → Return "Tomorrow"
  if (inputDate.toDateString() === tomorrow.toDateString()) {
    return i18next.t("date.tomorrow"); // Translation key for "Tomorrow"
  }

  // 🔹 Use appropriate date formatting based on the format type
  const formattedDate =
    format === "long"
      ? formatFullDate(inputDate, locale) // Full date format (e.g., "05/02/2025")
      : formatShortDateWithWeekday(inputDate, locale); // Short format (e.g., "Mon, 10 Feb")

  return i18next.t("date.colloquial", { val: formattedDate }); // Translation key for a formatted date
}
